import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { CATEGORY_TILES } from '../data/images';

const CategoryShowcase = () => {
  return (
    <section style={{ padding: '80px 0', background: '#ffffff' }}>
      <div className="container">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', maxWidth: '720px', margin: '0 auto 48px' }}
        >
          <span className="section-badge" style={{ letterSpacing: '0.12em' }}>GLOBAL DENIM COLLECTIONS</span>
          <h2 className="section-title" style={{ fontSize: 'clamp(28px, 4.5vw, 48px)' }}>
            EXPLORE <span className="gradient-text">CATEGORIES</span>
          </h2>
          <p className="section-subtitle" style={{ margin: '0 auto', fontSize: '16px' }}>
            Curated export collections engineered for global retail brands and international buyers.
          </p>
        </motion.div>

        {/* Visual Category Grid - American Eagle / Van Heusen Style */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: '20px'
          }}
        >
          {CATEGORY_TILES.map((cat, idx) => {
            // Span logic: 1st card 7 cols, 2nd card 5 cols; 3rd card 4 cols, 4th card 4 cols, 5th card 4 cols
            const colSpan = idx === 0 ? 7 : idx === 1 ? 5 : 4;
            return (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ duration: 0.6, delay: idx * 0.08 }}
                style={{
                  gridColumn: `span ${colSpan}`,
                  position: 'relative',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  minHeight: idx < 2 ? '360px' : '300px',
                  boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
                  cursor: 'pointer'
                }}
                className="category-card-tile"
              >
                <Link to={`/collections?category=${cat.id}`} style={{ textDecoration: 'none', display: 'block', height: '100%', width: '100%' }}>
                  {/* Image Background */}
                  <motion.img
                    src={cat.image}
                    alt={cat.title}
                    whileHover={{ scale: 1.07 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center center'
                    }}
                  />

                  {/* Gradient Overlay */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(to top, rgba(15, 23, 42, 0.85) 0%, rgba(15, 23, 42, 0.25) 50%, rgba(15, 23, 42, 0.1) 100%)',
                      zIndex: 1
                    }}
                  />

                  {/* Top Tag */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '16px',
                      left: '16px',
                      zIndex: 2,
                      background: 'rgba(255, 255, 255, 0.92)',
                      backdropFilter: 'blur(8px)',
                      padding: '5px 12px',
                      borderRadius: '99px',
                      fontSize: '10px',
                      fontWeight: 900,
                      color: '#0f172a',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase'
                    }}
                  >
                    {cat.tag}
                  </div>

                  {/* Bottom Text Content */}
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: '24px',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          fontSize: 'clamp(18px, 2.2vw, 24px)',
                          fontFamily: 'var(--font-heading)',
                          fontWeight: 900,
                          color: '#ffffff',
                          letterSpacing: '0.02em',
                          marginBottom: '4px',
                          textTransform: 'uppercase'
                        }}
                      >
                        {cat.title}
                      </h3>
                      <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.8)', margin: 0, fontWeight: 500, lineHeight: 1.6 }}>
                        <span style={{ display: 'block' }}>Color: {cat.color}</span>
                        <span style={{ display: 'block' }}>Fit: {cat.fit}</span>
                      </p>
                    </div>

                    <div
                      style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0f172a',
                        flexShrink: 0,
                        marginLeft: '12px'
                      }}
                    >
                      <ArrowUpRight size={18} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Responsive Grid Override */}
      <style>{`
        @media (max-width: 900px) {
          .category-card-tile { grid-column: span 6 !important; min-height: 280px !important; }
        }
        @media (max-width: 600px) {
          .category-card-tile { grid-column: span 12 !important; min-height: 240px !important; }
        }
      `}</style>
    </section>
  );
};

export default CategoryShowcase;
